<div class="flex h-full flex-col justify-between">
  @if (state.editingOptions && !state.editingOptions.hideQuestionDetailsHeader) {
    <div
      class="small:items-center small:flex-row z-2 bg-neutral-0 sticky top-0 flex flex-col flex-wrap justify-between gap-4 border-b border-neutral-200 p-2"
    >
      @if (state.totalMarks !== undefined) {
        <div class="font-primary text-primary text-md">[Maximum mark: {{ state.totalMarks }}]</div>
      }
      <div class="flex items-center justify-center gap-4">
        @if (state.difficulty) {
          <dojo-difficulty [difficulty]="state.difficulty"></dojo-difficulty>
        }
        @if (allowCalculatorEdit()) {
          <mat-button-toggle-group
            class="custom-toggle-group size-s"
            hideSingleSelectionIndicator
            [value]="calculatorAllowed() ? 'allowed' : 'not-allowed'"
            (change)="calculatorAllowedChange.emit($event.value === 'allowed')"
          >
            <mat-button-toggle value="allowed" [matTooltip]="'examdojo.question.calculator_allowed' | transloco">
              <img src="assets/images/calculator/calculator-allowed.svg" class="h-[20px] w-[20px]" />
            </mat-button-toggle>
            <mat-button-toggle
              value="not-allowed"
              [matTooltip]="'examdojo.question.calculator_not_allowed' | transloco"
            >
              <img src="assets/images/calculator/calculator-not-allowed.svg" class="h-[20px] w-[20px]" />
            </mat-button-toggle>
          </mat-button-toggle-group>
        } @else {
          @if (calculatorAllowed()) {
            <div
              class="flex h-[24px] w-[28px] items-center justify-center"
              [matTooltip]="'examdojo.question.calculator_allowed' | transloco"
            >
              <img src="assets/images/calculator/calculator-allowed.svg" class="max-h-full max-w-full" />
            </div>
          } @else {
            <div
              class="flex h-[24px] w-[28px] items-center justify-center"
              [matTooltip]="'examdojo.question.calculator_not_allowed' | transloco"
            >
              <img src="assets/images/calculator/calculator-not-allowed.svg" class="max-h-full max-w-full" />
            </div>
          }
        }
      </div>
    </div>
  }

  @if (state.editingOptions) {
    <div class="flex h-full gap-4">
      <div
        class="text-primary h-full w-full"
        cdkDropList
        #mainDropList="cdkDropList"
        [cdkDropListData]="questionItemsForm.controls"
        [cdkDropListConnectedTo]="allContainers()"
        (cdkDropListDropped)="drop($any($event))"
        [cdkDropListOrientation]="'vertical'"
        [dojoStemLabel]="questionItemsForm"
      >
        @for (item of questionItemsForm.controls; track $index; let questionItemIndex = $index) {
          <div
            class="draggable-item flex w-full items-start justify-between gap-1 p-3"
            [class.active]="activeQuestionItem()?.ctrl === item"
            [class.allow-question-editing]="!state.editingOptions.allowQuestionStructureEditing"
            cdkDrag
            [cdkDragData]="item"
            [cdkDragDisabled]="!state.editingOptions.allowQuestionStructureEditing"
            [dojoStemLabelIndex]="questionItemIndex"
          >
            @if (state.editingOptions.allowQuestionStructureEditing) {
              <dojo-icon-button
                [icon]="'grip-vertical'"
                [iconSize]="14"
                cdkDragHandle
                class="cursor-move-button -mt-0.5"
              />
            }

            <div class="w-full min-w-0 flex-1">
              @switch (item.value.type) {
                @case (QuestionItemTypeEnum.Stem) {
                  @if (stemViewerTemplate(); as stemViewerTemplate) {
                    <dojo-question-item-editor-stem-template-container
                      [stemViewerTemplate]="stemViewerTemplate"
                      [stem]="$any(item)"
                      [stemIndex]="$index"
                      [stemGroupIndex]="null"
                    />
                  } @else {
                    <dojo-question-item-editor-stem-viewer
                      [stemFormGroup]="$any(item)"
                      [stemIndex]="$index"
                      [isSelectingDisabled]="isSelectingDisabled()"
                      [markCalculationMethod]="markCalculationMethod()"
                    />
                  }
                }

                @case (QuestionItemTypeEnum.Stimulus) {
                  <div class="flex w-full justify-between gap-2">
                    <div class="w-full overflow-x-auto overflow-y-hidden">
                      <dojo-content-item-viewer
                        [contentItemsFormArray]="item.controls.content_items"
                        [isAllowedToEditImage]="state.editingOptions.allowImageEditing.length > 0"
                        [isAllowedToEditTable]="
                          state.editingOptions.allowImageEditing.length > 0 || state.editingOptions.allowQuestionEditing
                        "
                        (editItem)="editStimulusItem(item, $event, $index)"
                      />
                    </div>
                    @if (!state.editingOptions.hideBadges || state.editingOptions.allowQuestionEditing) {
                      <div
                        class="wrap-badge"
                        [ngStyle]="{
                          '--after-color':
                            'var(--' + CONTENT_ITEM_TYPE_TO_COLOR[QuestionItemTypeEnum.Stimulus] + '-200)'
                        }"
                      >
                        <dojo-selector-badge
                          class="self-start"
                          (clicked)="setActiveQuestionItem({ ctrl: item, type: QuestionItemTypeEnum.Stimulus }, $index)"
                          [color]="CONTENT_ITEM_TYPE_TO_COLOR[QuestionItemTypeEnum.Stimulus]"
                          [label]="CONTENT_ITEM_TYPE_TO_SHORT_LABEL[QuestionItemTypeEnum.Stimulus]"
                          [matTooltip]="CONTENT_ITEM_TYPE_TO_LONG_LABEL[QuestionItemTypeEnum.Stimulus]"
                          [active]="activeQuestionItem()?.ctrl === item"
                          [disabled]="isSelectingDisabled() || !state.editingOptions.allowQuestionEditing"
                        />
                      </div>
                    }
                  </div>
                }
                @case (QuestionItemTypeEnum.StemGroup) {
                  <div class="flex w-full items-start justify-between gap-1">
                    <dojo-stem-label class="stem-label min-h-6 min-w-7 empty:hidden md:mr-1" />

                    <div class="stem-group flex w-full flex-col gap-2">
                      <div class="flex w-full justify-between gap-2 overflow-x-auto overflow-y-hidden empty:hidden">
                        @if (item.controls.content_items.length > 0) {
                          <div class="w-full overflow-x-auto overflow-y-hidden">
                            <dojo-content-item-viewer
                              [contentItemsFormArray]="item.controls.content_items"
                              [isAllowedToEditImage]="state.editingOptions.allowImageEditing.length > 0"
                              [isAllowedToEditTable]="
                                state.editingOptions.allowImageEditing.length > 0 ||
                                state.editingOptions.allowQuestionEditing
                              "
                              (editItem)="editStemGroupItem(item, $event, $index)"
                            />
                          </div>
                        }
                        @if (!state.editingOptions.hideBadges || state.editingOptions.allowQuestionEditing) {
                          <div
                            class="wrap-badge ml-auto pr-3"
                            [ngStyle]="{
                              '--after-color':
                                'var(--' + CONTENT_ITEM_TYPE_TO_COLOR[QuestionItemTypeEnum.StemGroup] + '-200)'
                            }"
                          >
                            <dojo-selector-badge
                              class="self-start"
                              (clicked)="
                                setActiveQuestionItem({ ctrl: item, type: QuestionItemTypeEnum.StemGroup }, $index)
                              "
                              [color]="CONTENT_ITEM_TYPE_TO_COLOR[QuestionItemTypeEnum.StemGroup]"
                              [label]="CONTENT_ITEM_TYPE_TO_SHORT_LABEL[QuestionItemTypeEnum.StemGroup]"
                              [matTooltip]="CONTENT_ITEM_TYPE_TO_LONG_LABEL[QuestionItemTypeEnum.StemGroup]"
                              [active]="activeQuestionItem()?.ctrl === item"
                              [disabled]="isSelectingDisabled() || !state.editingOptions.allowQuestionEditing"
                            />
                          </div>
                        }
                      </div>

                      <div
                        cdkDropList
                        #stemGroup="cdkDropList"
                        [cdkDropListData]="$any(item.controls).stems"
                        [cdkDropListConnectedTo]="allContainers()"
                        (cdkDropListDropped)="dropSubStem($any($event), $any(item))"
                        [cdkDropListOrientation]="'vertical'"
                        [cdkDropListEnterPredicate]="stemGroupEnterPredicate"
                        class="min-h-[60px]"
                        [dojoStemLabel]="$any(item.controls).stems"
                        [labelsArray]="SUBSTEM_TAG_VALUES"
                      >
                        @if (item.controls.type.value === QuestionItemTypeEnum.StemGroup) {
                          @for (stem of $any(item.controls).stems.controls; track stemIndex; let stemIndex = $index) {
                            <div
                              class="draggable-item flex items-start gap-2 p-3"
                              [class.allow-question-editing]="!state.editingOptions.allowQuestionStructureEditing"
                              cdkDrag
                              [cdkDragData]="stem"
                              [cdkDragDisabled]="!state.editingOptions.allowQuestionStructureEditing"
                              [dojoStemLabelIndex]="stemIndex"
                            >
                              @if (state.editingOptions.allowQuestionStructureEditing) {
                                <dojo-icon-button
                                  [icon]="'grip-vertical'"
                                  [iconSize]="14"
                                  cdkDragHandle
                                  class="cursor-move-button -mt-0.5"
                                />
                              }
                              <div class="w-full min-w-0">
                                <div class="flex w-full flex-col gap-1">
                                  @if (stemViewerTemplate(); as stemViewerTemplate) {
                                    <dojo-question-item-editor-stem-template-container
                                      [stemViewerTemplate]="stemViewerTemplate"
                                      [stem]="stem"
                                      [stemIndex]="stemIndex"
                                      [stemGroupIndex]="questionItemIndex"
                                    />
                                  } @else {
                                    <dojo-question-item-editor-stem-viewer
                                      [stemFormGroup]="stem"
                                      [stemIndex]="stemIndex"
                                      [stemGroupIndex]="questionItemIndex"
                                      [isSelectingDisabled]="isSelectingDisabled()"
                                      [markCalculationMethod]="markCalculationMethod()"
                                    />
                                  }
                                </div>
                              </div>
                            </div>
                          }
                        }
                      </div>
                    </div>
                  </div>
                }
                @case (QuestionItemTypeEnum.Options) {
                  @if (optionsViewerTemplate(); as optionsViewerTemplate) {
                    <dojo-question-item-options-template-container
                      [optionsViewerTemplate]="optionsViewerTemplate"
                      [optionsFormGroup]="$any(item)"
                      [optionsIndex]="$index"
                    />
                  } @else {
                    <dojo-question-item-options-viewer
                      [optionsFormGroup]="$any(item)"
                      [isSelectingDisabled]="isSelectingDisabled()"
                      (editItem)="editOptionsItem(item, $event, $index)"
                      (setActiveQuestionItem)="
                        setActiveQuestionItem({ ctrl: item, type: QuestionItemTypeEnum.Options }, $index)
                      "
                    />
                  }
                }
              }
            </div>
          </div>
        }
      </div>
    </div>
  }
</div>
